import Collapse from '@mui/material/Collapse';
import { styled } from '@mui/material/styles';
import { DEFAULT_BORDER_RADIUS } from 'src/theme/constants';

export const TableLayout = styled('div', { name: 'TableLayout' })<{
  firstTab?: boolean;
  showBorder?: boolean;
}>(({ theme, firstTab, showBorder = false }) => ({
  height: '100%',
  width: '100%',
  display: 'grid',
  backgroundColor: theme.palette.background.paper,
  gridTemplateAreas: `
    "filters actions"
    "chips chips"
    "table table"`,
  gridTemplateRows: `${theme.spacing(56)} min-content auto`,

  [`:not(:has(${TableLayoutChipsRoot}))`]: {
    [`${TableLayoutTableRoot}`]: {
      marginTop: theme.spacing(16),
      '&:before': {
        content: '""',
        display: 'block',
        height: theme.spacing(16),
        marginTop: theme.spacing(-16),
        border: showBorder ? `1px solid ${theme.palette.border}` : 'none',
        borderBottom: 'none',
        borderTop: 'none',
      },
    },
  },

  [`:has(${TableLayoutTableRoot}:only-child)`]: {
    borderTopRightRadius: DEFAULT_BORDER_RADIUS,
    gridTemplateAreas: `"table"`,
    gridTemplateRows: 'auto',
  },

  [`& ${TableLayoutFilters}`]: {
    borderTopLeftRadius: firstTab ? 0 : DEFAULT_BORDER_RADIUS,
  },

  [`& ${TableLayoutTableRoot}:only-child`]: {
    marginTop: 0,
    borderTop: showBorder ? `1px solid ${theme.palette.border}` : 'none',
    borderTopRightRadius: DEFAULT_BORDER_RADIUS,
    '&:before': {
      display: 'none',
    },
    '&:after': {
      display: 'block',
      content: '""',
      right: 0,
      top: -1,
      position: 'absolute',
      width: theme.spacing(12),
      height: theme.spacing(12),
      border: showBorder ? `1px solid ${theme.palette.border}` : 'none',
      borderBottom: 'none',
      borderLeft: 'none',
      borderTopRightRadius: DEFAULT_BORDER_RADIUS,
    },
  },
}));

export const TableLayoutFilters = styled('div', { name: 'TableLayoutFilters' })<{
  showBorder?: boolean;
}>(({ theme, showBorder = false }) => ({
  boxSizing: 'border-box',
  gridArea: 'filters',
  display: 'flex',
  paddingTop: theme.spacing(17),
  paddingLeft: theme.spacing(17),
  gap: theme.spacing(16),
  border: showBorder ? `1px solid ${theme.palette.border}` : 'none',
  borderBottom: 'none',
  borderRight: 'none',
  borderTopLeftRadius: DEFAULT_BORDER_RADIUS,
}));

export const TableLayoutSearchInput = styled('div', { name: 'TableLayoutSearchInput' })(
  ({ theme }) => ({
    flex: 1,
    maxWidth: theme.spacing(360),
  }),
);

export const TableLayoutActions = styled('div', { name: 'TableLayoutActions' })<{
  showBorder?: boolean;
}>(({ theme, showBorder = false }) => ({
  gridArea: 'actions',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  paddingTop: theme.spacing(16),
  paddingRight: theme.spacing(16),
  gap: theme.spacing(16),
  border: showBorder ? `1px solid ${theme.palette.border}` : 'none',
  borderTopRightRadius: DEFAULT_BORDER_RADIUS,
  borderBottom: 'none',
  borderLeft: 'none',
}));

const TableLayoutChipsRoot = styled('div')<{ hide?: boolean; showBorder?: boolean }>(
  ({ theme, showBorder = false }) => ({
    gridArea: 'chips',
    display: 'flex',
    boxSizing: 'content-box',
    alignItems: 'center',
    paddingRight: theme.spacing(16),
    paddingLeft: theme.spacing(16),
    paddingBottom: theme.spacing(16),
    border: showBorder ? `1px solid ${theme.palette.border}` : 'none',
    borderBottom: 'none',
    borderTop: 'none',
  }),
);

const TableLayoutChipsLoader = styled('div')<{ hide?: boolean }>(({ theme }) => ({
  paddingTop: theme.spacing(8),
}));

export const TableLayoutChips = ({
  children,
  hide,
  showBorder,
}: {
  children: React.ReactNode;
  hide: boolean;
  showBorder?: boolean;
}) => {
  return (
    <TableLayoutChipsRoot showBorder={showBorder}>
      <Collapse in={!hide}>
        <TableLayoutChipsLoader>{children}</TableLayoutChipsLoader>
      </Collapse>
    </TableLayoutChipsRoot>
  );
};

const TableLayoutTableRoot = styled('div', { name: 'TableLayoutTable' })({
  position: 'relative',
  gridArea: 'table',
});

const TableLayoutTableContent = styled('div')({
  position: 'absolute',
  inset: '0',
});

export const TableLayoutTable = ({ children }: { children: React.ReactNode }) => {
  return (
    <TableLayoutTableRoot>
      <TableLayoutTableContent>{children}</TableLayoutTableContent>
    </TableLayoutTableRoot>
  );
};
